import * as React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { TimelineLite } from 'gsap';
import styled from 'styled-components';
import { setTimeline, setSelectedStep } from '../../store/Timeline/actions';
import { getTimeline } from '../../store/Timeline/reducer';

import { fonts, brandColors, device } from '../../styles/variables';

const Wrapper = styled.div`
  width: 100%;
  position: relative;
  margin-top: 15px;
`;

const NumberContainer = styled.div`
  pointer-events: all;
  display: block;
  ${({ lineLength }) => `
    margin: ${lineLength === 'large' ? '0 196px 50px 0' : '0 40px 50px 0'};
  `};
  ${({ dotMarkers }) =>
    dotMarkers === true &&
    `
    margin: 0 0 18px 0;
    float: none;
    text-align: center;
    @media ${device.tablet} {
      float: right;
    }
  `};

  @media ${device.tablet} {
    margin: 0 0 50px 0;
  }

  @media ${device.laptop} {
    float: none;
    ${({ lineLength }) => `
      margin: ${
        lineLength === 'xlarge'
          ? '0 196px 0 0'
          : lineLength === 'large'
            ? '0 40px 0 0'
            : '0 50px 0 0'
      };
    `};
    display: inline-block;
    ${({ alignment }) => `
      vertical-align: ${alignment};
    `};
  }
`;

const Number = styled.div`
  ${({ selected, numberColor, alignment, lineLength }) => `
    position: relative;
    cursor: pointer;
    width: 38px;
    height: 38px;
    margin: 1px 0;
    font-family: ${fonts.athelas};
    font-size: 15px;
    font-style: italic;
    letter-spacing: 2px;
    line-height: 38px;
    text-align: center;
    color: ${numberColor};
    display: inline-block;
    vertical-align: ${alignment};
    border: ${selected ? `1px solid ${numberColor}` : 'none'};
    border-radius: 50%;
    :after {
      content: '';
      display: ${selected ? 'block' : 'none'};
      background: ${numberColor};
      position: absolute;
      height: 48px;
      width: 1px;
      right: unset;
      top: unset;
      left: 50%;
      bottom: -42px;
    }

    @media ${device.laptop} {
      display: block;
      :after {
        display: ${selected ? 'block' : 'none'};
        height: 1px;
        width: ${lineLength === 'xlarge' ? '189px' : lineLength === 'large' ? '100px' : '48px'};
        right: ${lineLength === 'xlarge' ? '-183px' : lineLength === 'large' ? '-94px' : '-42px'};
        top: 20px;
        left: auto;
        bottom: auto;
      }
    }
  `};

  ${({ dotMarkers, numberColor, selected }) =>
    dotMarkers === true &&
    `
      width: ${selected ? '6px' : '4px'};
      height: ${selected ? '6px' : '4px'};
      background: ${selected ? 'none' : numberColor};
      margin: 0 5px;
      :after {
        display: none;
      }
      span {
        display: none;
      }

      @media ${device.tablet} {
        width: ${selected ? '8px' : '5px'};
        height: ${selected ? '8px' : '5px'};
        margin: 0 13px;
      }

      @media ${device.laptop} {
        width: 38px;
        height: 38px;
        background: none;
        :after {
          display: ${selected ? 'block' : 'none'};
        }
        span {
          display: block;
        }
      }
  `};
`;

const TimelineContent = styled.div`
  display: inline-block;
  max-width: 100%;
  ${({ alignment }) => `
    vertical-align: ${alignment};
  `};
  @media ${device.laptop} {
    max-width: 87%;
  }
  ${({ dotMarkers }) =>
    dotMarkers === true &&
    `
    display: block;
    text-align: center;
    @media ${device.tablet} {
      float: right;
      text-align: right;
    }
    @media ${device.laptop} {
      display: inline-block;
      float: none;
      text-align: left;
    }
  `};
`;

export const TimelineItemContainer = styled.div`
  .Title {
    margin-bottom: 0;
  }
`;

class Timeline extends React.PureComponent {
  componentUnmounted = false;

  constructor(props) {
    super(props);
    const { timelineId, children, addTimeline } = props;

    this.timelineInformation = {};
    this.nodeToShow = null;
    this.timelineContent = React.createRef();
    this.animationTimeline = new TimelineLite();
    this.timeout = null;

    // grab the nodes and update the store with the new timeline
    let nodes = children;
    if (!Array.isArray(children)) {
      nodes = [children];
    }
    addTimeline(timelineId, nodes);
  }

  componentDidMount() {
    const { timelineId, timeline } = this.props;
    this.timelineInformation = getTimeline(timeline, timelineId);
  }

  // Will only update the current timeline based on the timelineId
  componentDidUpdate(prevProps) {
    const { timelineId, timeline, automatic } = this.props;
    const previousTimeline = getTimeline(prevProps.timeline, timelineId);
    const newTimeline = getTimeline(timeline, timelineId);

    if (
      this.nodeToShow === null &&
      this.timelineInformation !== undefined &&
      Object.keys(this.timelineInformation).length !== 0
    ) {
      /* eslint-disable prefer-destructuring */
      this.nodeToShow = this.timelineInformation.nodes[0];
    }

    if (previousTimeline !== newTimeline) {
      this.timelineInformation = newTimeline;

      // need to allow for it to automatically loop through the timeline nodes.
      if (automatic) {
        this.timeout = setTimeout(() => {
          // need to get the next node and step
          const node = newTimeline.nodes[newTimeline.selectedStep];
          const numberOfNodes = newTimeline.nodes.length;
          let nextStep = newTimeline.selectedStep + 1;
          if (nextStep > numberOfNodes) {
            nextStep = 1;
          }
          this.showCorrectNode(node, nextStep);
        }, 5000);
      }
      this.forceUpdate();
    }
  }

  componentWillUnmount() {
    this.animationTimeline.stop();
    this.animationTimeline.clear();
    this.componentUnmounted = true;
  }

  showCorrectNode(childNode, selectedStep) {
    const { updateSelectedStep, timelineId } = this.props;

    if (childNode && !this.componentUnmounted) {
      // reset the timeline
      this.animationTimeline.pause(0);
      this.animationTimeline.clear();
      this.animationTimeline.restart();

      if (this.nodeToShow !== childNode && this.timelineContent.current !== childNode) {
        this.animationTimeline.to(this.timelineContent.current, 0.5, {
          opacity: 0,
          onComplete: () => {
            this.nodeToShow = childNode;
            this.animationTimeline.to(this.timelineContent.current, 0.5, { opacity: 1 });
            // if the selected step is passed update it in the store
            if (selectedStep) updateSelectedStep(timelineId, selectedStep);
          }
        });
      }
    }
  }

  render() {
    const { numberColor, alignment, dotMarkers, className, lineLength } = this.props;

    if (
      this.timelineInformation !== undefined &&
      Object.keys(this.timelineInformation).length !== 0
    ) {
      return (
        <Wrapper className={className}>
          <NumberContainer
            alignment={alignment}
            dotMarkers={dotMarkers}
            className="NumberContainer"
            lineLength={lineLength}
          >
            {this.timelineInformation.nodes.map((child, index) => (
              /* eslint-disable react/no-array-index-key */
              <Number
                key={index + 1}
                onMouseEnter={() => {
                  this.showCorrectNode(child, index + 1);
                  clearTimeout(this.timeout);
                }}
                selected={this.timelineInformation.selectedStep === index + 1}
                numberColor={numberColor}
                alignment={alignment}
                dotMarkers={dotMarkers}
                className="Number"
                lineLength={lineLength}
              >
                <span>{child.props.timelineStep || `0${index + 1}`}</span>
              </Number>
            ))}
          </NumberContainer>
          <TimelineContent
            className="TimelineContent"
            dotMarkers={dotMarkers}
            alignment={alignment}
            innerRef={this.timelineContent}
          >
            {(this.nodeToShow && React.cloneElement(this.nodeToShow)) ||
              React.cloneElement(this.timelineInformation.nodes[0])}
          </TimelineContent>
        </Wrapper>
      );
    }
    return <div>Loading..</div>;
  }
}

Timeline.defaultProps = {
  timelineId: '',
  automatic: false,
  timeline: {},
  numberColor: brandColors.white,
  alignment: 'top',
  className: '',
  dotMarkers: false,
  lineLength: 'normal'
};

Timeline.propTypes = {
  timelineId: PropTypes.string,
  automatic: PropTypes.bool,
  timeline: PropTypes.object,
  children: PropTypes.node.isRequired,
  addTimeline: PropTypes.func.isRequired,
  updateSelectedStep: PropTypes.func.isRequired,
  numberColor: PropTypes.string,
  alignment: PropTypes.string,
  className: PropTypes.string,
  dotMarkers: PropTypes.bool,
  lineLength: PropTypes.string
};

const mapStateToProps = state => ({
  timeline: state.timeline
});

export default connect(
  mapStateToProps,
  {
    addTimeline: setTimeline,
    updateSelectedStep: setSelectedStep
  }
)(Timeline);



// WEBPACK FOOTER //
// ./src/components/Timeline/index.jsx